import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { apiClient, setApiClientToken } from '../utils/apiClient';
import { normalizePermissions } from '../utils/accessControl';

const AuthContext = createContext();

const LAST_ACTIVITY_KEY = 'session_last_activity_ms';
const SESSION_START_KEY = 'session_started_at_ms';
const LOGOUT_REASON_KEY = 'auth_logout_reason';

const INACTIVITY_TIMEOUT_MS = Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_MS || 45 * 60 * 1000);
const SESSION_TIMEOUT_MS = Number(process.env.REACT_APP_SESSION_TIMEOUT_MS || 55 * 60 * 1000);
const SESSION_HEARTBEAT_MS = 30 * 1000;

const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart', 'touchmove'];

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddingLength = (4 - (normalized.length % 4)) % 4;
    const padded = normalized.padEnd(normalized.length + paddingLength, '=');
    const payload = window.atob(padded);
    return JSON.parse(payload);
  } catch (error) {
    return null;
  }
};

const setSessionMetadata = () => {
  const now = Date.now();
  localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  if (!localStorage.getItem(SESSION_START_KEY)) {
    localStorage.setItem(SESSION_START_KEY, String(now));
  }
};

const clearSessionMetadata = () => {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_START_KEY);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(normalizePermissions(null, {}));
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const fetchPermissions = useCallback(async (token, userData) => {
    if (!token || !userData?.id) {
      setPermissions(normalizePermissions(userData, {}));
      setPermissionsLoading(false);
      return;
    }

    setPermissionsLoading(true);
    try {
      const response = await apiClient.get('/permissions/me');
      setPermissions(normalizePermissions(userData, response.data || {}));
    } catch (error) {
      setPermissions(normalizePermissions(userData, {}));
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const logout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearSessionMetadata();
    if (reason) {
      localStorage.setItem(LOGOUT_REASON_KEY, String(reason));
    } else {
      localStorage.removeItem(LOGOUT_REASON_KEY);
    }
    setIsAuthenticated(false);
    setUser(null);
    setPermissions(normalizePermissions(null, {}));
    setPermissionsLoading(false);
    delete axios.defaults.headers.common.Authorization;
    setApiClientToken(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      const tokenPayload = decodeJwtPayload(token);
      const tokenExpMs = Number(tokenPayload?.exp || 0) * 1000;

      if (tokenExpMs && Date.now() >= tokenExpMs) {
        logout('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
      } else {
        try {
          const parsedUser = JSON.parse(userStr);
          setIsAuthenticated(true);
          setUser(parsedUser);
          setSessionMetadata();
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;
          setApiClientToken(token);
          fetchPermissions(token, parsedUser);
        } catch (error) {
          logout();
        }
      }
    } else {
      setIsAuthenticated(false);
    }

    setLoading(false);
  }, [fetchPermissions, logout]);

  useEffect(() => {
    const handleAuthError = (error) => {
      const statusCode = Number(error?.response?.status || 0);
      const messageText = String(error?.response?.data?.message || '').toLowerCase();
      const hasTokenError = messageText.includes('token') || messageText.includes('not authorized');

      if ((statusCode === 401 || statusCode === 403) && hasTokenError) {
        logout('Oturum sonlandı. Lütfen tekrar giriş yapın.');
      }

      return Promise.reject(error);
    };

    const interceptor = axios.interceptors.response.use((response) => response, handleAuthError);
    const apiClientInterceptor = apiClient.interceptors.response.use((response) => response, handleAuthError);

    return () => {
      axios.interceptors.response.eject(interceptor);
      apiClient.interceptors.response.eject(apiClientInterceptor);
    };
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const markUserActive = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markUserActive();
      }
    };

    const handleWindowFocus = () => {
      markUserActive();
    };

    const handleStorageChange = (event) => {
      if (event.key === 'token' && !event.newValue) {
        logout('Oturum sonlandı. Lütfen tekrar giriş yapın.');
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markUserActive, { passive: true });
    });
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    const timer = window.setInterval(() => {
      const now = Date.now();
      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      const sessionStartedAt = Number(localStorage.getItem(SESSION_START_KEY) || 0);
      const token = localStorage.getItem('token');
      const tokenPayload = decodeJwtPayload(token);
      const tokenExpMs = Number(tokenPayload?.exp || 0) * 1000;

      if (INACTIVITY_TIMEOUT_MS > 0 && lastActivity > 0 && now - lastActivity > INACTIVITY_TIMEOUT_MS) {
        logout('Guvenlik nedeniyle oturumunuz inaktivite sebebiyle kapatildi.');
        return;
      }

      if (SESSION_TIMEOUT_MS > 0 && sessionStartedAt > 0 && now - sessionStartedAt > SESSION_TIMEOUT_MS) {
        logout('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
        return;
      }

      if (tokenExpMs > 0 && now >= tokenExpMs) {
        logout('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
      }
    }, SESSION_HEARTBEAT_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markUserActive);
      });
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.clearInterval(timer);
    };
  }, [isAuthenticated, logout]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem(LOGOUT_REASON_KEY);
    clearSessionMetadata();
    setSessionMetadata();
    setIsAuthenticated(true);
    setUser(userData);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setApiClientToken(token);
    fetchPermissions(token, userData);
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (permissionKey) => Boolean(permissionKey && permissions[permissionKey]),
    [permissions]
  );

  const refreshPermissions = useCallback(() => {
    return fetchPermissions(localStorage.getItem('token'), user);
  }, [fetchPermissions, user]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        permissions,
        permissionsLoading,
        hasPermission,
        login,
        logout,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
